import L from 'leaflet'
import { EFlightAreaType } from '../types/flight-area'
import pin19be6b from '../assets/icons/pin-19be6b.svg'
import pin212121 from '../assets/icons/pin-212121.svg'
import pin2d8cf0 from '../assets/icons/pin-2d8cf0.svg'
import pinb620e0 from '../assets/icons/pin-b620e0.svg'
import pine23c39 from '../assets/icons/pin-e23c39.svg'
import pineffbb00 from '../assets/icons/pin-ffbb00.svg'
import { getRoot } from '/@/root'
import rootStore from '/@/store'
import { GeojsonCoordinate } from '/@/types/map'

export function useGMapCover () {
  const root = getRoot()
  const normalColor = '#2D8CF0'
  const store = rootStore
  const coverMap = store.state.coverMap

  const flightAreaColorMap: { [key in EFlightAreaType]: string } = {
    [EFlightAreaType.DFZ]: '#ff4d4f', // Red
    [EFlightAreaType.NFZ]: '#ff4d4f', // Red
    [EFlightAreaType.ALARM]: '#faad14', // Yellow
    [EFlightAreaType.WARNING]: '#1890ff', // Blue
  }
  const disableColor = '#b3b3b3'

  const colorIconMap: Record<string, string> = {
    '2d8cf0': pin2d8cf0,
    '19be6b': pin19be6b,
    212121: pin212121,
    b620e0: pinb620e0,
    e23c39: pine23c39,
    ffbb00: pineffbb00,
  }

  function getMap (): L.Map {
    return root.$map
  }

  function getId (layer: any): string {
    return (layer as any)._extData?.id
  }

  function AddCoverToMap (layer: L.Layer, id: string) {
    const map = getMap()
    if (!map) return
    map.addLayer(layer)
    if (!coverMap[id]) coverMap[id] = []
    coverMap[id].push(layer)
  }

  function getPinIcon (color?: string): L.Icon {
    const key = (color?.replace(/#/g, '') || '').toLowerCase()
    return L.icon({
      iconUrl: colorIconMap[key] || pin2d8cf0,
      iconSize: [32, 32],
      iconAnchor: [16, 32],
    })
  }

  function init2DPin (name: string, coordinates: GeojsonCoordinate, color?: string, data?: {}) {
    const marker = L.marker(
      [coordinates[1] as number, coordinates[0] as number],
      { icon: getPinIcon(color), title: name }
    )
    ;(marker as any)._extData = data
    AddCoverToMap(marker, (data as any)?.id)
  }

  function initPolyline (name: string, coordinates: GeojsonCoordinate[], color?: string, data?: {}) {
    const latlngs = coordinates.map(c => [c[1], c[0]] as L.LatLngTuple)
    const polyline = L.polyline(latlngs, {
      color: color || normalColor,
      weight: 4,
    })
    ;(polyline as any)._extData = data
    AddCoverToMap(polyline, (data as any)?.id)
  }

  function initPolygon (name: string, coordinates: GeojsonCoordinate[][], color?: string, data?: {}) {
    const latlngs = coordinates[0].map(c => [c[1], c[0]] as L.LatLngTuple)
    const polygon = L.polygon(latlngs, {
      color: color || normalColor,
      fillColor: color || normalColor,
      fillOpacity: 0.4,
      weight: 2,
    })
    ;(polygon as any)._extData = data
    AddCoverToMap(polygon, (data as any)?.id)
  }

  function removeCoverFromMap (id: string) {
    const map = getMap()
    if (!map) return
    if (coverMap[id]) {
      coverMap[id].forEach((layer: L.Layer) => map.removeLayer(layer))
      coverMap[id] = []
    }
    const arrowId = `${id}-arrows`
    if (coverMap[arrowId]) {
      coverMap[arrowId].forEach((layer: L.Layer) => map.removeLayer(layer))
      coverMap[arrowId] = []
    }
  }

  function getElementFromMap (id: string): any[] {
    return coverMap[id] || []
  }

  function updatePinElement (id: string, name: string, coordinates: GeojsonCoordinate, color?: string) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const el = elements[0] as L.Marker
      el.setLatLng([coordinates[1] as number, coordinates[0] as number])
      el.setIcon(getPinIcon(color))
      ;(el as any)._extData.color = color
    } else {
      init2DPin(name, coordinates, color, { id, name })
    }
  }

  function drawDirectionalArrows (id: string, coordinates: GeojsonCoordinate[], color?: string) {
    const map = getMap()
    if (!map || coordinates.length < 2) return

    const arrowColor = color || normalColor
    // 약 20개의 화살표만 표시하도록 간격 조절
    const interval = Math.max(1, Math.floor(coordinates.length / 20))
    const arrowId = `${id}-arrows`

    // 이전에 생성된 화살표 레이어 그룹이 있다면 제거
    const existingGroups = getElementFromMap(arrowId)
    if (existingGroups.length > 0) {
      existingGroups.forEach((group: any) => map.removeLayer(group))
      coverMap[arrowId] = []
    }

    const arrowGroup = L.layerGroup()
    const lastPoint: L.LatLng | null = null

    for (let i = 0; i < coordinates.length - 1; i += interval) {
      const p1 = coordinates[i]
      const p2 = coordinates[i + 1]

      // 비행 경로 화살표를 선분의 중간 지점에 표시하여 시각적 안정성 확보
      // 좌표 유효성 및 숫자 타입 강제 변환
      const n1 = { lat: Number(p1[1]), lon: Number(p1[0]) }
      const n2 = { lat: Number(p2[1]), lon: Number(p2[0]) }

      if (isNaN(n1.lat) || isNaN(n1.lon) || isNaN(n2.lat) || isNaN(n2.lon)) continue

      const lat1 = n1.lat * Math.PI / 180
      const lon1 = n1.lon * Math.PI / 180
      const lat2 = n2.lat * Math.PI / 180
      const lon2 = n2.lon * Math.PI / 180

      const midLat = (n1.lat + n2.lat) / 2
      const midLon = (n1.lon + n2.lon) / 2
      const midPoint = L.latLng(midLat, midLon)

      // 너무 짧은 구간(노이즈) 혹은 정지 비행 중일 때는 방향 계산에서 제외 (최소 3m 이상 이동 시에만 표시)
      const startPoint = L.latLng(n1.lat, n1.lon)
      const endPoint = L.latLng(n2.lat, n2.lon)
      if (startPoint.distanceTo(endPoint) < 3.0) continue

      const y = Math.sin(lon2 - lon1) * Math.cos(lat2)
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)
      const bearing = (Math.atan2(y, x) * 180 / Math.PI + 360) % 360

      const icon = L.divIcon({
        html: `<svg viewBox="0 0 24 24" width="20" height="20"
                 fill="${arrowColor}" stroke="#fff" stroke-width="1.5"
                 style="display:block; pointer-events:none; overflow:visible;">
                 <g transform="rotate(${bearing}, 12, 12)">
                   <path d="M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z"/>
                 </g>
               </svg>`,
        className: 'leaflet-arrow-icon',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })

      const marker = L.marker(midPoint, { icon, interactive: false })
      arrowGroup.addLayer(marker)
    }

    arrowGroup.addTo(map)
    if (!coverMap[arrowId]) coverMap[arrowId] = []
    coverMap[arrowId].push(arrowGroup)
  }

  function updatePolylineElement (id: string, name: string, coordinates: GeojsonCoordinate[], color?: string) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const el = elements[0] as L.Polyline
      el.setStyle({ color: color || normalColor })
      el.setLatLngs(coordinates.map(c => [c[1], c[0]] as L.LatLngTuple))
      ;(el as any)._extData.color = color || normalColor
    } else {
      initPolyline(name, coordinates, color, { id, name })
    }
    // 화살표 방향 표시 추가
    drawDirectionalArrows(id, coordinates, color)
  }

  /**
   * 요소의 위치로 지도를 이동 및 확대
   */
  function flyToPath (id: string) {
    const map = getMap()
    if (!map) return
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const el = elements[0] as L.Polyline
      const bounds = el.getBounds()
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { padding: [50, 50], maxZoom: 18 })
      }
    }
  }

  function updatePolygonElement (id: string, name: string, coordinates: GeojsonCoordinate[][], color?: string) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const el = elements[0] as L.Polygon
      el.setStyle({ color: color || normalColor, fillColor: color || normalColor })
      const latlngs = coordinates[0].map(c => [c[1], c[0]] as L.LatLngTuple)
      el.setLatLngs(latlngs)
      ;(el as any)._extData.color = color || normalColor
    } else {
      initPolygon(name, coordinates, color, { id, name })
    }
  }

  function initTextInfo (content: string, coordinates: GeojsonCoordinate, id: string) {
    const map = getMap()
    if (!map) return
    const tooltip = L.tooltip({ permanent: true, direction: 'top', className: 'map-text-label' })
      .setContent(content)
      .setLatLng([coordinates[1] as number, coordinates[0] as number])
    ;(tooltip as any)._extData = { id, type: 'text' }
    map.addLayer(tooltip)
    if (!coverMap[id]) coverMap[id] = []
    coverMap[id].push(tooltip)
  }

  function initCircle (name: string, radius: number, position: GeojsonCoordinate, color?: string, data?: {}) {
    const circle = L.circle(
      [position[1] as number, position[0] as number],
      {
        radius,
        color: color || normalColor,
        fillColor: color || normalColor,
        fillOpacity: 0.4,
        weight: 2,
      }
    )
    ;(circle as any)._extData = data
    AddCoverToMap(circle, (data as any)?.id)
  }

  function updateCircleElement (id: string, name: string, radius: number, position: GeojsonCoordinate, color?: string) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const el = elements[0] as L.Circle
      el.setRadius(radius)
      el.setLatLng([position[1] as number, position[0] as number])
      el.setStyle({ color: color || normalColor, fillColor: color || normalColor })
      ;(el as any)._extData.color = color || normalColor
    } else {
      initCircle(name, radius, position, color, { id, name })
    }
  }

  function initFlightAreaCircle (
    name: string, radius: number, position: GeojsonCoordinate,
    data: { id: string, type: EFlightAreaType, enable: boolean },
    color?: string
  ) {
    const areaColor = color || (data.enable ? flightAreaColorMap[data.type] : disableColor)
    const circle = L.circle(
      [position[1] as number, position[0] as number],
      {
        radius,
        color: areaColor,
        fillColor: color || flightAreaColorMap[data.type],
        fillOpacity: EFlightAreaType.NFZ === data.type && data.enable ? 0.3 : 0,
        dashArray: '10, 5',
        weight: 4,
      }
    )
    ;(circle as any)._extData = data
    AddCoverToMap(circle, data.id)
    initTextInfo(name, position, data.id)
  }

  function updateFlightAreaCircle (
    id: string, name: string, radius: number, position: GeojsonCoordinate,
    enable: boolean, type: EFlightAreaType, color?: string
  ) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const areaColor = color || (enable ? flightAreaColorMap[type] : disableColor)
      const textIndex = elements.findIndex((e: any) => e._extData?.type === 'text')
      if (textIndex === -1) { initTextInfo(name, position, id) } else {
        const tooltip = elements[textIndex] as L.Tooltip
        tooltip.setContent(name).setLatLng([position[1] as number, position[0] as number])
      }
      const circle = elements[textIndex ^ 1] as L.Circle
      circle.setRadius(radius)
      circle.setLatLng([position[1] as number, position[0] as number])
      circle.setStyle({
        color: areaColor,
        fillColor: color || areaColor,
        fillOpacity: EFlightAreaType.NFZ === type && enable ? 0.3 : 0,
      })
    } else {
      initFlightAreaCircle(name, radius, position, { id, type, enable }, color)
    }
  }

  function calcPolygonPosition (coordinate: GeojsonCoordinate[]): GeojsonCoordinate {
    const index = coordinate.length - 1
    return [(coordinate[0][0] as number + (coordinate[index][0] as number)) / 2.0, (coordinate[0][1] as number + (coordinate[index][1] as number)) / 2]
  }

  function initFlightAreaPolygon (
    name: string, coordinates: GeojsonCoordinate[],
    data: { id: string, type: EFlightAreaType, enable: boolean },
    color?: string
  ) {
    const areaColor = color || (data.enable ? flightAreaColorMap[data.type] : disableColor)
    const latlngs = coordinates.map(c => [c[1], c[0]] as L.LatLngTuple)
    const polygon = L.polygon(latlngs, {
      color: areaColor,
      fillColor: color || flightAreaColorMap[data.type],
      fillOpacity: EFlightAreaType.NFZ === data.type && data.enable ? 0.3 : 0,
      dashArray: '10, 5',
      weight: 4,
    })
    ;(polygon as any)._extData = data
    AddCoverToMap(polygon, data.id)
    initTextInfo(name, calcPolygonPosition(coordinates), data.id)
  }

  function updateFlightAreaPolygon (
    id: string, name: string, coordinates: GeojsonCoordinate[],
    enable: boolean, type: EFlightAreaType, color?: string
  ) {
    const elements = getElementFromMap(id)
    if (elements.length > 0) {
      const areaColor = color || (enable ? flightAreaColorMap[type] : disableColor)
      const textIndex = elements.findIndex((e: any) => e._extData?.type === 'text')
      if (textIndex === -1) { initTextInfo(name, calcPolygonPosition(coordinates), id) } else {
        const tooltip = elements[textIndex] as L.Tooltip
        tooltip.setContent(name).setLatLng([calcPolygonPosition(coordinates)[1] as number, calcPolygonPosition(coordinates)[0] as number])
      }
      const polygon = elements[textIndex ^ 1] as L.Polygon
      polygon.setLatLngs(coordinates.map(c => [c[1], c[0]] as L.LatLngTuple))
      polygon.setStyle({
        color: areaColor,
        fillColor: color || areaColor,
        fillOpacity: EFlightAreaType.NFZ === type && enable ? 0.3 : 0,
      })
    } else {
      initFlightAreaPolygon(name, coordinates, { id, type, enable }, color)
    }
  }

  return {
    init2DPin,
    initPolyline,
    initPolygon,
    removeCoverFromMap,
    getElementFromMap,
    updatePinElement,
    updatePolylineElement,
    updatePolygonElement,
    initCircle,
    updateCircleElement,
    initFlightAreaCircle,
    initFlightAreaPolygon,
    updateFlightAreaPolygon,
    updateFlightAreaCircle,
    calcPolygonPosition,
    flyToPath,
  }
}
